"use client";

import { useState } from "react";
import { useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";

type Stance = "argue" | "inform" | "compare";

const STANCES: { value: Stance; label: string }[] = [
  { value: "argue", label: "Argue" },
  { value: "inform", label: "Inform" },
  { value: "compare", label: "Compare" },
];

type Frame = (main: string, support: string) => string;

const SENTENCE_FRAMES: Record<Stance, Frame[]> = {
  argue: [
    (m, s) => `${m} should be taken seriously because ${s}.`,
    (m, s) =>
      `Although some disagree, ${m} is justified by the fact that ${s}.`,
    (m, s) => `The strongest reason to support ${m} is that ${s}.`,
    (m, s) => `Because ${s}, ${m} deserves our support.`,
    (m, s) => `The evidence that ${s} shows why ${m} is the right position.`,
    (m, s) => `${m} matters precisely because ${s}.`,
  ],
  inform: [
    (m, s) => `One key aspect of ${m} is that ${s}.`,
    (m, s) => `${m} can be understood through the fact that ${s}.`,
    (m, s) => `To understand ${m}, it helps to first examine how ${s}.`,
    (m, s) => `${m} is shaped in large part by the fact that ${s}.`,
    (m, s) => `A closer look at ${m} reveals that ${s}.`,
    (m, s) => `${m} involves several factors, most notably that ${s}.`,
  ],
  compare: [
    (m, s) => `In terms of ${s}, ${m} shows more contrast than first appears.`,
    (m, s) => `${m} can best be compared through ${s}.`,
    (m, s) => `Examining ${s} reveals how the two sides of ${m} differ.`,
    (m, s) =>
      `While the similarities in ${m} are clear, ${s} exposes the key differences.`,
    (m, s) => `${s} offers the clearest lens for comparing ${m}.`,
    (m, s) =>
      `Both sides of ${m} share common ground, yet ${s} sets them apart.`,
  ],
};

function sentenceCase(text: string): string {
  if (text.length === 0) {
    return text;
  }
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function buildSentences(
  main: string,
  support: string,
  stance: Stance
): string[] {
  return SENTENCE_FRAMES[stance].map((frame) =>
    sentenceCase(frame(main, support))
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const TOPIC_SENTENCE_GENERATOR_STRINGS = {
  mainIdeaLabel: "Main idea of the paragraph",
  mainIdeaPlaceholder: "e.g. banning phones in classrooms",
  supportingPointLabel: "Supporting point or reason",
  supportingPointPlaceholder:
    "e.g. students concentrate better without notifications",
  stanceLabels: {
    argue: "Argue",
    inform: "Inform",
    compare: "Compare",
  },
  stanceQuestion: "What is the paragraph doing?",
  generate: "Generate Topic Sentences",
  resultsHeading:
    "Sentence frames — smooth the wording so each reads naturally in your voice",
  copy: "Copy",
  copied: "Copied!",
} as const;

export default function TopicSentenceGeneratorWidget() {
  const strings = useToolUi(TOPIC_SENTENCE_GENERATOR_STRINGS);
  const [mainIdea, setMainIdea] = useState("");
  const [supportingPoint, setSupportingPoint] = useState("");
  const [stance, setStance] = useState<Stance>("argue");
  const [sentences, setSentences] = useState<string[]>([]);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const ready = mainIdea.trim().length > 0 && supportingPoint.trim().length > 0;

  function handleGenerate() {
    if (!ready) {
      return;
    }
    setSentences(
      buildSentences(mainIdea.trim(), supportingPoint.trim(), stance)
    );
    setCopiedIndex(null);
  }

  function handleCopy(sentence: string, index: number) {
    writeToClipboard(sentence).then((copied) => {
      if (!copied) {
        return;
      }

      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    });
  }

  return (
    <>
      {/* Main idea */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="ts-main-idea"
        >
          {strings.mainIdeaLabel}
        </label>
        <input
          className="w-full rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="ts-main-idea"
          onChange={(e) => setMainIdea(e.target.value)}
          placeholder={strings.mainIdeaPlaceholder}
          type="text"
          value={mainIdea}
        />
      </div>

      {/* Supporting point */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="ts-supporting-point"
        >
          {strings.supportingPointLabel}
        </label>
        <input
          className="w-full rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="ts-supporting-point"
          onChange={(e) => setSupportingPoint(e.target.value)}
          placeholder={strings.supportingPointPlaceholder}
          type="text"
          value={supportingPoint}
        />
      </div>

      {/* Stance */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">
          {strings.stanceQuestion}
        </p>
        <div className="flex flex-wrap gap-3">
          {STANCES.map((s) => (
            <label
              className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              key={s.value}
            >
              <input
                checked={stance === s.value}
                className="accent-primary"
                name="ts-stance"
                onChange={() => setStance(s.value)}
                type="radio"
                value={s.value}
              />
              {strings.stanceLabels[s.value]}
            </label>
          ))}
        </div>
      </section>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={!ready}
          onClick={handleGenerate}
          type="button"
        >
          {strings.generate}
        </button>
      </div>

      {/* Results */}
      {sentences.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-medium text-foreground text-sm">
            {strings.resultsHeading}
          </h2>
          <ul className="space-y-3">
            {sentences.map((sentence, index) => (
              <li
                className="flex items-start justify-between gap-4 rounded-lg border border-border bg-card p-5"
                key={sentence}
              >
                <p className="text-foreground text-sm leading-relaxed">
                  {sentence}
                </p>
                <button
                  className="shrink-0 rounded-lg border border-border bg-card px-3 py-1.5 font-medium text-foreground text-xs transition-colors hover:bg-muted"
                  onClick={() => handleCopy(sentence, index)}
                  type="button"
                >
                  {copiedIndex === index ? strings.copied : strings.copy}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}
